import * as React from 'react';

import { Message as AIMessage } from '@ai-sdk/react';

import { Preview as MarkdownPreview } from '@components/common/MarkdownEditor';
import { TiptapRenderer } from '@components/shared/TiptapRenderer';

import { ToolInvocation } from './types';
import { generateDraftStudyFromToolArgs } from './utils';

interface ToolContentProps {
  message: AIMessage;
}

export const ToolContent: React.FC<ToolContentProps> = ({ message }) => {
  const toolResult = (message.toolInvocations as ToolInvocation[])
    ?.slice()
    .reverse()
    .find(
      (invocation) => invocation.toolName === 'save_study_plan_to_scratchpad' && invocation.args !== undefined
    )?.args;

  if (!toolResult) {
    return null;
  }

  // Extract interview guide if present
  const draftStudy =
    toolResult && typeof toolResult === 'object' && 'draft_study' in toolResult ? toolResult.draft_study : null;

  const interviewGuide =
    draftStudy && typeof draftStudy === 'object' && 'interview_guide' in draftStudy
      ? (draftStudy as any).interview_guide
      : null;

  // Determine content based on toolResult structure
  let content: string;

  if (typeof toolResult === 'string') {
    content = toolResult;
  } else if (
    toolResult &&
    typeof toolResult === 'object' &&
    Object.keys(toolResult).length === 1 &&
    'draft_study' in toolResult
  ) {
    content = generateDraftStudyFromToolArgs(toolResult);
  } else {
    content = JSON.stringify(toolResult, null, 2);
  }

  return (
    <div className='px-4 py-2 text-left text-gray-900'>
      {/* Existing metadata as markdown */}
      <div className='prose prose-sm max-w-none text-sm leading-relaxed'>
        <MarkdownPreview className='text-sm'>{content}</MarkdownPreview>
      </div>

      {/* Interview guide section */}
      {interviewGuide && (
        <div className='mt-6 border-t border-gray-200 pt-4'>
          <h4 className='mb-3 text-sm font-semibold text-gray-900'>Interview Guide</h4>
          <TiptapRenderer
            document={interviewGuide}
            config={{
              artifacts: true,
              tables: true,
              image: { enable: true },
              link: { enable: true },
              highlight: { enable: true, readOnly: true },
              headings: true
            }}
            className='prose prose-sm ProseMirror max-w-none text-sm leading-relaxed'
          />
        </div>
      )}
    </div>
  );
};
